import { useEffect, useState, useCallback } from "react";
import apiService from "../../../../services/api";
import { useAuth } from "../../../../contexts/AuthContext";
import { Job, User } from "../../../../types";
import { JobFormData, JobMockFormData, ToastState } from "../utils/job_types";
import { createJob, getJobsDetails, getJobsList } from "../api/job_api";
import { JobStatus } from "../model/jobEnums";

export function useJobManagementManager() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [jobs, setJobs] = useState<JobMockFormData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);

  const [jobDetailsData, setJobDetailsData] = useState<JobMockFormData | null>(
    null
  );
  const [formData, setFormData] = useState<JobFormData>({
    title: "",
    department: "",
    location: "",
    hiring_manager: "",
    recruiter: "",
    type: "full-time",
    remote: false,
    salary_min: "",
    salary_max: "",
    description: "",
    benefits: "",
    requirements: "",
    skills: [],
    experience_level: "mid",
    job_function: "",
    vacancies: 0,
    visa_sponsorship: false,
    salary_currency: "",
    salary_period: "annuly",
    education_level: "",
    relocation_offered: false,
    travel_percentage: 0,
    target_start_date: "",
    hiring_urgency: "",
    interview_process: "",
    external_url: "",
    application_email: "",
    application_deadline: "",
    max_applications: 2,
    posting_visibility: "",
    is_featured: true,
    approval_flow_id: "",
    meta_title: "",
    meta_description: "",
    tags: "",
    work_mode: "Remote",
    status: "draft",
  });
  const [showAddForm, setShowAddForm] = useState(false);
  const [toast, setToast] = useState<ToastState>({
    isVisible: false,
    message: "",
    type: "success",
  });

  const showToast = (
    message: string,
    type: "success" | "error" | "warning" | "info"
  ) => {
    setToast({ isVisible: true, message, type });
  };

  const hideToast = () => {
    setToast((prev) => ({ ...prev, isVisible: false }));
  };

  const [skills, setSkills] = useState<any[]>([]);
  const [newSkill, setNewSkill] = useState({
    skill_name: "",
    proficiency_level: "beginner",
    minimum_years: 0,
    is_required: false,
  });

  const handleAddSkill = () => {
    if (!newSkill.skill_name.trim()) return;

    setSkills((prev) => [
      ...prev,
      {
        ...newSkill,
        skill_id: crypto.randomUUID(),
      },
    ]);

    setNewSkill({
      skill_name: "",
      is_required: false,
      minimum_years: 0,
      proficiency_level: "beginner",
    });
  };
  const handleDeleteSkill = (skillId: string) => {
    setSkills((prev) => prev.filter((skill) => skill.skill_id !== skillId));
  };
  const getStatusColor = (status: Job["status"]) => {
    switch (status) {
      case "active":
        return "bg-green-100 text-green-800";
      case "paused":
        return "bg-yellow-100 text-yellow-800";
      case "draft":
        return "bg-gray-100 text-gray-800";
      case "closed":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };


  const fetchJobsList = async (params?: {
    status?: string;
    visibility?: string;
    department?: string;
    hiring_manager_id?: string;
    recruiter_id?: string;
    limit?: number;
    offset?: number;
  }) => {
    try {
      setLoading(true);
      setError(null);
      const resp = await getJobsList(params || {});
      console.log("get jobs", resp.data);
      setJobs(resp.data);
    } catch (err) {
      console.log(err);
      setError("Failed to fetch jobs");
    } finally {
      setLoading(false);
    }
  };
  const fetchJobDetails = async (id: any) => {
    try {
      setLoading(true);

      const resp = await getJobsDetails(id);
      setJobDetailsData(resp.data);
      console.log(resp.data);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };
  const handleCreateJob = async () => {
    const formatDateToISO = (dateString: string) => {
      if (!dateString) return null;
      return new Date(dateString + 'T00:00:00.000Z').toISOString();
    };

    const payload = {
      title: formData.title.trim(),

      hiring_manager_id: formData.hiring_manager,
      recruiter_id: formData.recruiter || null,

      slug: formData.title
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, "-"),

      description:
        formData.description && formData.description.trim().length > 0
          ? formData.description
          : "N/A",
      requirements: formData.requirements || null,

      responsibilities: formData.recruiter || "N/A",

      benefits: formData.benefits || null,

      job_type: "full_time",
      work_mode: "remote",
      experience_level: formData.experience_level || "entry",

      department: formData.department || null,
      job_function: formData.job_function || null,
      location: formData.location || null,

      salary_min: Number(formData.salary_min || 0),
      salary_max: Number(formData.salary_max || 0),
      salary_currency: "USD",
      salary_period: "annual",

      education_level: formData.education_level || null,
      vacancies: Number(formData.vacancies || 1),

      visa_sponsorship: Boolean(formData.visa_sponsorship),
      relocation_offered: Boolean(formData.relocation_offered),
      travel_percentage: Number(formData.travel_percentage || 0),

      target_start_date: formatDateToISO(formData.target_start_date),
      // hiring_urgency: formData.hiring_urgency || "normal",
      hiring_urgency:
        formData.hiring_urgency &&
        ["immediate", "within_30_days", "within_60_days", "flexible"].includes(
          formData.hiring_urgency
        )
          ? formData.hiring_urgency
          : "flexible",

      interview_process: formData.interview_process || null,
      external_url: formData.external_url || null,
      application_email: formData.application_email || null,
      application_deadline: formatDateToISO(formData.application_deadline),

      max_applications: Number(formData.max_applications || 0),

      posting_visibility: "both",

      is_featured: Boolean(formData.is_featured),

      approval_flow_id: formData.approval_flow_id || null,

      meta_title: formData.meta_title || null,
      meta_description: formData.meta_description || null,

      tags: Array.isArray(formData.tags) ? formData.tags : [],

      skills: skills.map((s) => ({
        skill_id: s.skill_id,
        skill_name: s.skill_name,
        is_required: Boolean(s.is_required),
        minimum_years: Number(s.minimum_years || 0),
        proficiency_level: s.proficiency_level,
      })),

      status: JobStatus.UnderReview,
    };

    console.log("FINAL PAYLOAD:", payload);

    try {
      setLoading(true);
      const response = await createJob(payload);
      console.log("SUCCESS:", response);
      showToast("Job created successfully", "success");
      setShowModal(false);
      await fetchJobsList();
    } catch (error: any) {
      console.error("API ERROR:", error);
    }finally{
      setLoading(false);
    }
  };

  return {
    users,
    setUsers,
    currentUser,
    formData,
    setFormData,
    handleCreateJob,
    handleAddSkill,
    skills,
    newSkill,
    setNewSkill,
    handleDeleteSkill,
    loading,
    setLoading,
    error,
    setError,
    jobs,
    setJobs,
    showToast,
    hideToast,
    showModal,
    setShowModal,
    toast,
    setToast,
    fetchJobDetails,
    jobDetailsData,
    setJobDetailsData,
    getStatusColor,
    fetchJobsList,
  };
}
